import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

// Uma movimentação do recebedor (venda, estorno, taxa...). "nomeServicoFormatado" é a forma de
// pagamento (Pix, Cartão de crédito...) e "dataEfetivacao" é a data em que o valor cai na conta.
export interface TransacaoPagarmeModel {
    id: string
    pedidoFormatado: string | null
    nomeServicoFormatado: string | null
    dataCriacao: string | null
    dataEfetivacao: string | null
    nomeSituacao: string | null
    valorBrutoFormatado: string | null
    valorTaxaFormatado: string | null
    valorLiquidoFormatado: string | null
    valorLiquido: number | null
    entrada: boolean
    estorno: boolean
    estornarHabilitado: boolean
}

// Movimentações agrupadas pelo repasse em que caíram (ou "pendente", ainda sem repasse).
export interface GrupoExtratoPagarmeModel {
    pendente: boolean
    identificador: string
    dataExibicao: string
    valorTransferenciaFormatado: string
    totalVendasFormatado: string
    totalEstornosFormatado: string
    quantidadeVendas: number
    quantidadeEstornos: number
    componentes: TransacaoPagarmeModel[]
}

export interface ExtratoPagarmeResponse {
    grupos: GrupoExtratoPagarmeModel[]
    saldo: string
}

// O backend devolve "Cartao de Credito" sem acento; padroniza para exibição.
const FORMAS: Record<string, string> = {
    "cartao de credito": "Cartão de crédito",
    "cartao de debito": "Cartão de débito",
    pix: "Pix",
    boleto: "Boleto",
}

export const formatarFormaPagamento = (forma: string | null | undefined): string => {
    if (!forma) return "-"
    return FORMAS[forma.trim().toLowerCase()] ?? forma
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data.codErro !== undefined && data.codErro > 0) {
        return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

export const obterExtratoPagarme = async function (
    authorization: string,
    dataInicio: string,
    dataFim: string
): Promise<ExtratoPagarmeResponse | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<ExtratoPagarmeResponse>>(
                `pagarme/ObterExtratoPagarme?dataInicio=${dataInicio}&dataFim=${dataFim}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authorization,
                    },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        const res = tratamentoResponse<ExtratoPagarmeResponse>(response, "ObterExtratoPagarme")
        if ("retornoErro" in res) return res
        return { grupos: Array.isArray(res.grupos) ? res.grupos : [], saldo: res.saldo ?? "" }
    } catch (error) {
        RegistrarLogErro(error, "ObterExtratoPagarme", `${dataInicio} - ${dataFim}`)
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const estornarPixPagarme = async function (
    authorization: string,
    id: string
): Promise<{ mensagem: string } | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<{ mensagem: string }>>(
                `pagarme/EstornarPixPagarme/${id}`,
                {},
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authorization,
                    },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<{ mensagem: string }>(response, "EstornarPixPagarme")
    } catch (error) {
        RegistrarLogErro(error, "EstornarPixPagarme", id)
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
